import { Link } from 'react-router-dom';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const COOKIE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Cookie Policy — Discovering Marche',
  description: 'Informativa sull\'utilizzo dei cookie sul sito discoveringmarche.com ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003.',
  url: `${SITE_URL}/cookie-policy`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Cookie Policy', item: `${SITE_URL}/cookie-policy` },
    ],
  },
};

const cookieTable = [
  {
    category: 'Tecnici necessari',
    color: 'bg-stone-50 border-stone-200',
    badge: 'Sempre attivi',
    badgeColor: 'text-stone-500 bg-stone-100',
    desc: 'Indispensabili per il funzionamento del sito. Non raccolgono dati personali identificabili e non possono essere disattivati.',
    cookies: [
      { name: 'marche_casa_cookie_consent', provider: 'discoveringmarche.com', purpose: 'Memorizza le preferenze di consenso cookie dell\'utente', duration: '12 mesi', type: 'HTTP Cookie' },
      { name: 'i18next', provider: 'discoveringmarche.com', purpose: 'Memorizza la lingua preferita dall\'utente', duration: 'Sessione', type: 'HTTP Cookie' },
    ],
  },
  {
    category: 'Cookie analitici',
    color: 'bg-amber-50 border-amber-100',
    badge: 'Previo consenso',
    badgeColor: 'text-amber-700 bg-amber-100',
    desc: 'Raccolgono informazioni aggregate e anonime sull\'utilizzo del sito (pagine visitate, tempo di permanenza, provenienza del traffico) per migliorare l\'esperienza di navigazione.',
    cookies: [
      { name: '_ga', provider: 'Google Analytics', purpose: 'Distingue gli utenti unici assegnando un numero identificativo generato casualmente', duration: '2 anni', type: 'HTTP Cookie' },
      { name: '_ga_*', provider: 'Google Analytics', purpose: 'Mantiene lo stato della sessione', duration: '2 anni', type: 'HTTP Cookie' },
      { name: '_gid', provider: 'Google Analytics', purpose: 'Registra un ID univoco per generare dati statistici sull\'utilizzo del sito', duration: '24 ore', type: 'HTTP Cookie' },
    ],
  },
  {
    category: 'Cookie di marketing',
    color: 'bg-rose-50 border-rose-100',
    badge: 'Previo consenso',
    badgeColor: 'text-rose-700 bg-rose-100',
    desc: 'Utilizzati per tracciare i visitatori tra i siti web e mostrare annunci pertinenti e personalizzati. Vengono attivati solo previo consenso esplicito.',
    cookies: [
      { name: '_fbp', provider: 'Meta (Facebook)', purpose: 'Utilizzato da Facebook per fornire una serie di prodotti pubblicitari', duration: '3 mesi', type: 'HTTP Cookie' },
      { name: 'fr', provider: 'Meta (Facebook)', purpose: 'Utilizzato da Facebook per mostrare annunci pertinenti', duration: '3 mesi', type: 'HTTP Cookie' },
    ],
  },
];

const CookiePolicyPage = () => {
  const openConsentPanel = () => {
    window.dispatchEvent(new CustomEvent('openCookieConsent'));
  };

  return (
    <main className="min-h-screen bg-ivory-50">
      <SeoHead
        title="Cookie Policy — Discovering Marche | GDPR"
        description="Informativa sull'utilizzo dei cookie sul sito discoveringmarche.com ai sensi del GDPR e del D.Lgs. 196/2003. Scopri quali cookie utilizziamo e come gestirli."
        keywords="cookie policy, GDPR, cookie tecnici, cookie analitici, Discovering Marche"
        canonical="/cookie-policy"
        jsonLd={COOKIE_JSONLD}
      />

      {/* Hero */}
      <section className="bg-anthracite-800 pt-36 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">
            D.Lgs. 196/2003 · Regolamento UE 2016/679
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-white font-light leading-tight mb-6">
            Cookie Policy
          </h1>
          <p className="font-sans text-sm text-white/50 leading-relaxed mb-8">
            Informativa sull&apos;utilizzo dei cookie su <strong className="text-white/70">www.discoveringmarche.com</strong> ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003.
            Ultimo aggiornamento: <strong className="text-white/70">aprile 2026</strong>
          </p>
          <button
            onClick={openConsentPanel}
            className="inline-block font-sans text-xs tracking-widest uppercase px-7 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
          >
            Gestisci le tue preferenze
          </button>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto space-y-14">

          {/* 1. Cosa sono */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              1. Cosa sono i cookie
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>
                I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell&apos;utente (computer, smartphone, tablet) durante la navigazione. Vengono inviati dal server al browser e poi rimandati al server ad ogni visita successiva.
              </p>
              <p>
                I cookie permettono al sito di ricordare le azioni e le preferenze dell&apos;utente (come la lingua, le dimensioni dei caratteri e altre impostazioni di visualizzazione) in modo che non debbano essere reinserite ogni volta che si visita il sito o si naviga da una pagina all&apos;altra.
              </p>
            </div>
          </div>

          {/* 2. Tipologie */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              2. Tipologie di cookie utilizzati
            </h2>
            <div className="space-y-8">
              {cookieTable.map((cat) => (
                <div key={cat.category} className={`rounded-xl border p-6 ${cat.color}`}>
                  <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                    <h3 className="font-serif text-lg text-anthracite-800">{cat.category}</h3>
                    <span className={`font-sans text-xs px-3 py-1 rounded-full font-medium ${cat.badgeColor}`}>
                      {cat.badge}
                    </span>
                  </div>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-5">{cat.desc}</p>

                  {/* Cookie table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-black/10">
                          <th className="pb-2 pr-4 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal whitespace-nowrap">Nome</th>
                          <th className="pb-2 pr-4 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal whitespace-nowrap">Provider</th>
                          <th className="pb-2 pr-4 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal">Finalità</th>
                          <th className="pb-2 pr-4 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal whitespace-nowrap">Durata</th>
                          <th className="pb-2 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal whitespace-nowrap">Tipo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-black/5">
                        {cat.cookies.map((c) => (
                          <tr key={c.name}>
                            <td className="py-2.5 pr-4 font-mono text-xs text-anthracite-700 whitespace-nowrap">{c.name}</td>
                            <td className="py-2.5 pr-4 font-sans text-xs text-anthracite-500 whitespace-nowrap">{c.provider}</td>
                            <td className="py-2.5 pr-4 font-sans text-xs text-anthracite-400 leading-relaxed">{c.purpose}</td>
                            <td className="py-2.5 pr-4 font-sans text-xs text-anthracite-500 whitespace-nowrap">{c.duration}</td>
                            <td className="py-2.5 font-sans text-xs text-anthracite-400 whitespace-nowrap">{c.type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Cookie di terze parti */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              3. Cookie di terze parti
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>
                Alcuni cookie sono impostati da servizi di terze parti che appaiono sulle nostre pagine. Per questi cookie, le terze parti sono responsabili del trattamento dei dati. Ti invitiamo a consultare le rispettive informative:
              </p>
              <div className="space-y-2 mt-4">
                {[
                  { name: 'Google Analytics', url: 'https://policies.google.com/privacy', label: 'Privacy Policy Google' },
                  { name: 'Meta (Facebook)', url: 'https://www.facebook.com/privacy/policy/', label: 'Privacy Policy Meta' },
                ].map((item) => (
                  <div key={item.name} className="flex gap-4 items-center">
                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                    <span>
                      <strong className="text-anthracite-700">{item.name}</strong> —{' '}
                      <a
                        href={item.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="text-terracotta-400 hover:text-terracotta-500 transition-colors"
                      >
                        {item.label}
                      </a>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 4. Come gestire */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              4. Come gestire i cookie
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-4">
              <p>
                Puoi gestire le tue preferenze sui cookie in qualsiasi momento attraverso due modalità:
              </p>

              {/* Pannello preferenze */}
              <div className="p-5 bg-white rounded-xl border border-ivory-200 flex gap-4 items-start">
                <div className="w-9 h-9 flex items-center justify-center shrink-0 rounded-full bg-terracotta-50 border border-terracotta-100">
                  <i className="ri-settings-3-line text-terracotta-400 text-sm"></i>
                </div>
                <div className="flex-1">
                  <p className="font-sans text-sm font-semibold text-anthracite-700 mb-1">Pannello preferenze del sito</p>
                  <p className="font-sans text-xs text-anthracite-400 leading-relaxed mb-3">
                    Puoi modificare le tue preferenze in qualsiasi momento cliccando su &quot;Gestisci Cookie&quot; nel footer del sito, oppure direttamente qui:
                  </p>
                  <button
                    onClick={openConsentPanel}
                    className="font-sans text-xs tracking-widest uppercase px-5 py-2 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
                  >
                    Gestisci preferenze
                  </button>
                </div>
              </div>

              {/* Browser */}
              <div className="p-5 bg-white rounded-xl border border-ivory-200 flex gap-4 items-start">
                <div className="w-9 h-9 flex items-center justify-center shrink-0 rounded-full bg-terracotta-50 border border-terracotta-100">
                  <i className="ri-global-line text-terracotta-400 text-sm"></i>
                </div>
                <div className="flex-1">
                  <p className="font-sans text-sm font-semibold text-anthracite-700 mb-1">Impostazioni del browser</p>
                  <p className="font-sans text-xs text-anthracite-400 leading-relaxed mb-3">
                    Puoi configurare il tuo browser per bloccare o eliminare i cookie. Tieni presente che disabilitare i cookie tecnici potrebbe compromettere il corretto funzionamento del sito.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { name: 'Chrome', url: 'https://support.google.com/chrome/answer/95647' },
                      { name: 'Firefox', url: 'https://support.mozilla.org/it/kb/Gestione%20dei%20cookie' },
                      { name: 'Safari', url: 'https://support.apple.com/it-it/guide/safari/sfri11471/mac' },
                      { name: 'Edge', url: 'https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' },
                    ].map((b) => (
                      <a
                        key={b.name}
                        href={b.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs px-3 py-1.5 border border-ivory-200 text-anthracite-500 hover:border-terracotta-200 hover:text-terracotta-400 transition-colors rounded-full whitespace-nowrap"
                      >
                        {b.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Opt-out */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              5. Opt-out dai servizi di terze parti
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>Per disattivare i cookie di profilazione di terze parti puoi utilizzare i seguenti strumenti:</p>
              <div className="space-y-2 mt-2">
                {[
                  { label: 'Google Analytics Opt-out', url: 'https://tools.google.com/dlpage/gaoptout' },
                  { label: 'Your Online Choices (IAB Europe)', url: 'https://www.youronlinechoices.com/it/' },
                  { label: 'Network Advertising Initiative', url: 'https://optout.networkadvertising.org/' },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4 items-center">
                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-terracotta-400 hover:text-terracotta-500 transition-colors"
                    >
                      {item.label}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 6. Modifiche */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              6. Modifiche alla Cookie Policy
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed">
              <p>
                Il Titolare si riserva il diritto di modificare la presente Cookie Policy in qualsiasi momento, in particolare in caso di variazioni normative o tecnologiche. Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento. Si consiglia di consultare periodicamente questa pagina.
              </p>
            </div>
          </div>

          {/* 7. Contatti */}
          <div className="bg-white rounded-xl border border-ivory-200 p-8">
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-2">
              Titolare del trattamento
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-1 mb-6">
              <p><strong className="text-anthracite-700">Ines Felicity Di Nallo</strong> — P.IVA 03036380420</p>
              <p>Castelfidardo (AN), Italia</p>
              <p>
                Email:{' '}
                <a href="mailto:ines-felicity.di-nallo@iad-italia.it" className="text-terracotta-400 hover:text-terracotta-500 transition-colors">
                  ines-felicity.di-nallo@iad-italia.it
                </a>
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/privacy-policy"
                className="inline-block font-sans text-xs tracking-widest uppercase px-6 py-3 border border-terracotta-300 text-terracotta-300 hover:bg-terracotta-50 transition-colors rounded-full cursor-pointer whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                to="/contatti"
                className="inline-block font-sans text-xs tracking-widest uppercase px-6 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
              >
                Contattaci
              </Link>
            </div>
          </div>

        </div>
      </section>
    </main>
  );
};

export default CookiePolicyPage;
